import { Link } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';

export default function Cart() {
  const { items, removeItem, updateQuantity, subtotal, clearCart } = useCart();

  const tax = subtotal * 0.08;
  const shipping = subtotal >= 50 ? 0 : 9.99;
  const total = subtotal + tax + shipping;

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] py-12" data-testid="cart-page">
        <div className="max-w-[1440px] mx-auto px-4 text-center">
          <ShoppingBag size={64} className="mx-auto text-slate-300 mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Your cart is empty</h1>
          <p className="text-slate-500 mb-6">Looks like you haven't added anything yet.</p>
          <Link to="/products">
            <Button className="bg-[#003366] hover:bg-[#002855]" data-testid="continue-shopping-btn">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8" data-testid="cart-page">
      <div className="max-w-[1440px] mx-auto px-4">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">Shopping Cart</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-lg border p-4 flex gap-4"
                data-testid={`cart-item-${item.id}`}
              >
                <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-slate-100">
                  <img
                    src={item.images?.[0] || '/placeholder.png'}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <Link
                    to={`/products/${item.slug}`}
                    className="font-medium text-slate-900 hover:text-[#003366] line-clamp-2"
                  >
                    {item.name}
                  </Link>
                  <p className="text-lg font-bold text-[#B12704] mt-1">
                    ${item.price.toFixed(2)}
                  </p>

                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border rounded">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-2 hover:bg-slate-100"
                        data-testid={`decrease-${item.id}`}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="px-4 font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-2 hover:bg-slate-100"
                        data-testid={`increase-${item.id}`}
                      >
                        <Plus size={16} />
                      </button>
                    </div>

                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-[#EF4444] hover:text-[#DC2626] p-2"
                      data-testid={`remove-${item.id}`}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-between items-center pt-4">
              <Link to="/products">
                <Button variant="outline">Continue Shopping</Button>
              </Link>
              <Button
                variant="ghost"
                onClick={clearCart}
                className="text-[#EF4444] hover:text-[#DC2626] hover:bg-red-50"
                data-testid="clear-cart-btn"
              >
                Clear Cart
              </Button>
            </div>
          </div>

          {/* Order summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg border p-6 sticky top-24">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Order Summary</h2>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Subtotal ({items.length} items)</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Estimated Tax</span>
                  <span className="font-medium">${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Shipping</span>
                  <span className="font-medium">
                    {shipping === 0 ? (
                      <span className="text-[#10B981]">FREE</span>
                    ) : (
                      `$${shipping.toFixed(2)}`
                    )}
                  </span>
                </div>
                
                {subtotal < 50 && (
                  <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                    Add ${(50 - subtotal).toFixed(2)} more for free shipping
                  </p>
                )}

                <hr />

                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-[#003366]">${total.toFixed(2)}</span>
                </div>
              </div>

              <Link to="/checkout" data-testid="proceed-checkout-btn">
                <Button className="w-full mt-6 bg-[#FFD700] hover:bg-[#F4C430] text-slate-900 font-bold py-6 text-lg">
                  Proceed to Checkout <ArrowRight size={20} className="ml-2" />
                </Button>
              </Link>

              <div className="mt-4 text-center text-xs text-slate-500">
                <p>Secure checkout powered by Square</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
